/**
 * MessageRow — one item in the inbox list (left pane).
 *
 * Gmail-style three-line row: avatar (rounded-lg, category-tinted) on the
 * left; sender + category marker + timestamp on the first line; bold summary
 * on the second; a short preview chip/snippet on the third.
 *
 * Approve / Reject actions live exclusively in the reading pane — this row
 * carries no inline buttons.
 */
import { cn } from "@flint/ui";
import {
  type InboxItem,
  kindCategory,
  requesterInitials,
  requesterLabel,
  snapshotChips,
  whyText,
} from "./inbox-model.ts";
import { formatRelativeTime } from "../../lib/format.ts";

const CATEGORY_DOT: Record<string, string> = {
  warn: "bg-amber-400",
  bad: "bg-rose-500",
  info: "bg-sky-500",
};

const CATEGORY_TEXT: Record<string, string> = {
  warn: "text-amber-600",
  bad: "text-rose-600",
  info: "text-sky-600",
};

const AVATAR_BG: Record<string, string> = {
  warn: "bg-amber-100 text-amber-700",
  bad: "bg-rose-100 text-rose-700",
  info: "bg-sky-100 text-sky-700",
};

const SELECTED_RAIL: Record<string, string> = {
  warn: "before:bg-amber-400",
  bad: "before:bg-rose-500",
  info: "before:bg-sky-500",
};

interface MessageRowProps {
  item: InboxItem;
  selected: boolean;
  isFounder: boolean;
  onSelect: () => void;
  onDecide: (decision: "approved" | "rejected") => void;
  decidePending: boolean;
}

export function MessageRow({ item, selected, onSelect }: MessageRowProps) {
  const cat = kindCategory(item.kind);
  const chips = snapshotChips(item.kind, item.requestPayload);
  const isPending = item.status === "pending";

  const preview =
    chips.length > 0
      ? chips
          .slice(0, 2)
          .map((c) => `${c.label} ${c.value}`)
          .join(" · ")
      : whyText(item.kind).slice(0, 90);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        "relative w-full cursor-pointer border-b border-line px-4 py-3.5 text-left transition-colors",
        "before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:rounded-r",
        selected
          ? cn("bg-sky-50 before:opacity-100", SELECTED_RAIL[cat.tone])
          : "hover:bg-raised before:opacity-0",
      )}
    >
      <div className="flex items-start gap-3">
        {/* Avatar — rounded-lg, category-tinted */}
        <div
          className={cn(
            "mt-0.5 grid size-9 shrink-0 place-items-center rounded-lg text-xs font-bold tracking-wide",
            AVATAR_BG[cat.tone],
          )}
        >
          {requesterInitials(item)}
        </div>

        <div className="min-w-0 flex-1">
          {/* Line 1: sender · category marker · timestamp */}
          <div className="flex items-center gap-1.5">
            <span
              className={cn(
                "truncate text-xs text-ink",
                isPending ? "font-bold" : "font-medium",
              )}
            >
              {requesterLabel(item)}
            </span>
            <span
              className={cn(
                "flex shrink-0 items-center gap-1 text-xs font-semibold",
                CATEGORY_TEXT[cat.tone],
              )}
            >
              <span className={cn("size-1.5 shrink-0 rounded-full", CATEGORY_DOT[cat.tone])} />
              {cat.label}
            </span>
            <span className="ml-auto shrink-0 font-mono text-xs text-muted">
              {formatRelativeTime(item.createdAt)}
            </span>
          </div>

          {/* Line 2: summary */}
          <p
            className={cn(
              "mt-[3px] truncate text-xs leading-snug",
              isPending ? "font-semibold text-ink" : "font-normal text-ink-2",
            )}
          >
            {item.summary}
          </p>

          {/* Line 3: preview */}
          <p className="mt-[3px] truncate text-xs leading-snug text-muted">{preview}</p>
        </div>
      </div>
    </div>
  );
}
